/**
 * Acknowledgement reporting for administrators.
 *
 * Two different questions, answered two different ways.
 *
 * "Who is behind?" is an operational question asked daily, and it only needs
 * the OUTSTANDING records — 512 of the 2,033 in the reference organization.
 * That is read directly, because an administrator is allowed to by the rules
 * and because the answer has to be current.
 *
 * "Prove this policy was acknowledged" is the surveyor's question, and it goes
 * through `getAcknowledgementReport`, which resolves the version, counts
 * against the roster, and returns the completed rows as well as the missing
 * ones. That is evidence rather than a to-do list.
 *
 * SCALING, honestly: the outstanding read is bounded by staff × unacknowledged
 * policies. At the reference hospital that is hundreds; at five thousand staff
 * and six hundred policies it would not be, and this page would need the
 * per-policy aggregate instead. Noted rather than solved, because solving it
 * now would be guessing at a shape no customer has yet.
 */

import { calendarDate } from '../../shared/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { call } from '../../lib/callable';

const db = getFirestore(app);

export interface OutstandingAck {
  id: string;
  policyId: string;
  policyNumber: string;
  policyTitle: string;
  userId: string;
  userName: string;
  userEmail: string;
  status: 'pending' | 'overdue';
  dueDate: string | null;
}

/** One policy's outstanding total, for the index. */
export interface PolicyOutstanding {
  policyId: string;
  policyNumber: string;
  policyTitle: string;
  pending: number;
  overdue: number;
}

/** A date-only value, read in UTC. One rule, in shared/dates.ts. */
const toIsoDate = calendarDate;

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' && v ? v : fallback;
}

/** Everything still owed, across the organization. */
export function useOutstandingAcks(orgId: string | null) {
  return useQuery({
    queryKey: ['outstanding-acks', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<OutstandingAck[]> => {
      const snap = await getDocs(
        query(
          collection(db, 'orgs', orgId!, 'acknowledgements'),
          where('status', 'in', ['pending', 'overdue']),
        ),
      );

      return snap.docs.map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          policyId: str(d.policyId),
          policyNumber: str(d.policyNumber),
          policyTitle: str(d.policyTitle, '(untitled policy)'),
          userId: str(d.userId),
          userName: str(d.userDisplayName, str(d.userEmail, 'Unknown user')),
          userEmail: str(d.userEmail),
          status: d.status === 'overdue' ? 'overdue' : 'pending',
          dueDate: toIsoDate(d.dueDate),
        };
      });
    },
  });
}

/** Roll outstanding records up per policy, worst first. */
export function groupByPolicy(acks: readonly OutstandingAck[]): PolicyOutstanding[] {
  const byPolicy = new Map<string, PolicyOutstanding>();

  for (const a of acks) {
    const row = byPolicy.get(a.policyId) ?? {
      policyId: a.policyId,
      policyNumber: a.policyNumber,
      policyTitle: a.policyTitle,
      pending: 0,
      overdue: 0,
    };
    if (a.status === 'overdue') row.overdue += 1;
    else row.pending += 1;
    byPolicy.set(a.policyId, row);
  }

  // Overdue decides the order: those are the ones a surveyor could ask about
  // today, and a policy with forty overdue matters more than one with forty
  // merely pending.
  return [...byPolicy.values()].sort(
    (x, y) => y.overdue - x.overdue || y.pending - x.pending,
  );
}

// ---------------------------------------------------------------------------
// The per-policy report
// ---------------------------------------------------------------------------

export interface AckReportRow {
  userId: string;
  userName: string;
  /** "Reyes, Dana": what the report prints, in the order HR lists people. */
  userSortName?: string;
  userEmail: string;
  status: string;
  acknowledgedAt: string | null;
  dueDate: string | null;
  /**
   * Whether StandingDocs watched this person acknowledge the policy, or the
   * evidence came across from the system they used before. The two are not the
   * same claim and the report must not make them look it.
   */
  source?: 'standingdocs' | 'legacy_import';
  legacyAcknowledgementId?: string | null;
  /** What the attestation import recorded: the old system and the date given there. */
  legacyNote?: string | null;
}

export interface LegacyAttestation {
  id: string; name: string; email: string | null; employeeId: string | null; memberToday: boolean;
  versionLabel: string | null; attestedAt: string | null; sourceSystem: string | null; countedTowardCurrent: boolean;
}

export interface AckReport {
  policyId: string;
  policyTitle: string;
  policyNumber: string;
  versionId: string | null;
  versionNumber: number | null;
  stats: {
    total: number;
    pending: number;
    completed: number;
    overdue: number;
    completionRate: number;
  };
  acknowledgements: AckReportRow[];
  hasMore: boolean;
  /** Attestations kept by the system the organization came from, for this policy; not collected here. */
  legacy?: LegacyAttestation[];
  legacyTotal?: number;
}

/**
 * The report for a policy, or for ONE VERSION of it.
 *
 * The callable has accepted a version id from the beginning and nothing ever
 * sent one, so the page could only ever show the version in force. That made
 * historical evidence unreachable: once everybody had acknowledged a version
 * the policy dropped off the outstanding list, and after a new version
 * published there was no route at all to who had signed the old one.
 */
export function useAckReport(policyId: string | undefined, versionId?: string | null) {
  return useQuery({
    queryKey: ['ack-report', policyId, versionId ?? null],
    enabled: Boolean(policyId),
    retry: false,
    queryFn: () =>
      call<{ policyId: string; versionId?: string; includeCompleted: boolean; limit: number }, AckReport>(
        'getAcknowledgementReport',
        { policyId: policyId!, ...(versionId ? { versionId } : {}), includeCompleted: true, limit: 500 },
      ),
  });
}

/**
 * The acknowledgement document's id is deterministic — `{userId}_{versionId}`
 * — which is what lets the report waive a row without carrying ids around.
 */
export function ackIdFor(userId: string, versionId: string | null): string | null {
  return versionId ? `${userId}_${versionId}` : null;
}

/** Excuse one person from one acknowledgement, with a reason on record. */
export function useWaiveAcknowledgement(policyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { acknowledgementId: string; reason: string }) =>
      call<typeof input, unknown>('waiveAcknowledgement', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ack-report', policyId] });
      void qc.invalidateQueries({ queryKey: ['outstanding-acks'] });
    },
  });
}

/** The report as a file, for the survey binder or the HR folder. */
export async function exportAckCsv(policyId: string): Promise<{ csv: string; filename: string }> {
  return call<{ policyId: string }, { csv: string; filename: string }>('exportAcknowledgementsCsv', { policyId });
}

/** Hand the browser a file to save. Inert in tests, harmless there. */
export function saveTextFile(name: string, text: string, type = 'text/csv'): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
